import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import flow from 'lodash/flow'
import partialRight from 'lodash/partialRight'
import pickBy from 'lodash/pickBy'
import reduce from 'lodash/reduce'
import upperFirst from 'lodash/upperFirst'
import { withStyles } from '@material-ui/core/styles'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import {
  colorBlackSelector,
  colorWhiteSelector,
  getColorErrorSelector,
  getColorPrimarySelector,
  getColorSecondarySelector,
  getColorSuccessSelector,
  getColorTextSelector,
  getSignalFontVariantName,
  isSignalFontVariant,
  typographySelector
} from '@signal/theme'
import { TypographyBase } from './styles'

export const TYPOGRAPHY_COLORS = {
  BLACK: 'black',
  TEXT_PRIMARY: 'textPrimary',
  TEXT_SECONDARY: 'textSecondary',
  TEXT_DARK: 'textDark',
  PRIMARY: 'primary',
  PRIMARY_DARK: 'primaryDark',
  SECONDARY: 'secondary',
  SUCCESS: 'success',
  ERROR: 'error',
  INVERSE: 'inverse',
  INHERIT: 'inherit'
}

export const TYPOGRAPHY_VARIANTS = {
  H1: 'h1',
  H2: 'h2',
  H3: 'h3',
  H4: 'h4',
  H5: 'h5',
  H6: 'h6',
  SUBTITLE_1: 'subtitle1',
  SUBTITLE_2: 'subtitle2',
  BODY_1: 'body1',
  BODY_2: 'body2',
  BODY_3: 'body3',
  BODY_4: 'body4',
  CAPTION: 'caption'
}

/**
 * Boosts the specificity of the CSS selectors used for variants and colors
 * so that they are higher than the MUI ones; that way our styles are always
 * prioritized by browsers.
 */
const boostCssSpecificity = style => ({
  '&&': style
})

const signalVariantsSelector = partialRight(pickBy, (definition, key) =>
  isSignalFontVariant(key)
)

/**
 * Gets Signal font variants from the theme.
 */
const getFontVariants = flow(typographySelector, signalVariantsSelector)

/**
 * Defines typography colors and selectors to get their values from the theme.
 */
export const colorSelectors = {
  [TYPOGRAPHY_COLORS.BLACK]: colorBlackSelector,
  [TYPOGRAPHY_COLORS.TEXT_PRIMARY]: getColorTextSelector('primary'),
  [TYPOGRAPHY_COLORS.TEXT_SECONDARY]: getColorTextSelector('secondary'),
  [TYPOGRAPHY_COLORS.TEXT_DARK]: getColorTextSelector('dark'),
  [TYPOGRAPHY_COLORS.PRIMARY]: getColorPrimarySelector('main'),
  [TYPOGRAPHY_COLORS.PRIMARY_DARK]: getColorPrimarySelector('dark'),
  [TYPOGRAPHY_COLORS.SECONDARY]: getColorSecondarySelector('main'),
  [TYPOGRAPHY_COLORS.SUCCESS]: getColorSuccessSelector('main'),
  [TYPOGRAPHY_COLORS.ERROR]: getColorErrorSelector('main'),
  [TYPOGRAPHY_COLORS.INVERSE]: colorWhiteSelector,
  [TYPOGRAPHY_COLORS.INHERIT]: () => 'inherit'
}

/**
 * Gets the key to use for a given color name.
 */
const getColorKey = color => `color${upperFirst(color)}`

/**
 * Gets Signal font colors from the theme.
 */
const getColorStyles = theme =>
  reduce(
    colorSelectors,
    (colorStyles, colorSelector, color) => ({
      ...colorStyles,
      [getColorKey(color)]: boostCssSpecificity({
        color: colorSelector(theme)
      })
    }),
    {}
  )

/**
 * Defines styles that will be passed as `classes` to Typography.
 */
const styles = theme => ({
  ...getFontVariants(theme),
  ...getColorStyles(theme)
})

/**
 * This wraps the standard material ui Typography component and intercepts
 * a couple of props to allow for custom definitions of colors and variants.
 */
export class Typography extends React.PureComponent {
  static propTypes = {
    /**
     * This string will be used to get the color class.
     * @default 'textPrimary'
     * @type {string}
     */
    color: PropTypes.oneOf(Object.keys(colorSelectors).concat('default')),

    /**
     * The name of the custom signal font variant to use.
     * variant is fully intercepted and never passed to muiTypography. If you want
     * to use material ui's base variants, then use the prop muiVariant.
     * This allows us to define a custom set of font types.
     * @default 'body2'
     * @type {string}
     */
    variant: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf(Object.values(TYPOGRAPHY_VARIANTS))
    ]),

    /**
     * This prop will be passed to the wrapped MuiTypography component so that
     * its variants can be used, if necessary. It's probably uncommon, if ever,
     * that this will be used.
     * @type {string}
     */
    muiVariant: PropTypes.oneOf([
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'subtitle1',
      'body1',
      'body2',
      'caption',
      'button'
    ]),

    /**
     * We are empirically mapping the variant property to a range of different
     * DOM element types. For instance, h1 to <h1>. If you wish to change
     * that mapping, you can provide your own. Alternatively, you can use the
     * component property. The default mapping is the following:
     * @default {
     *   h1: 'h1',
     *   h2: 'h2',
     *   h3: 'h3',
     *   h4: 'h4',
     *   h5: 'h5',
     *   h6: 'h6',
     *   subtitle1: 'h6',
     *   subtitle2: 'p',
     *   body1: 'p',
     *   body2: 'p',
     *   body3: 'p',
     *   body4: 'p',
     *   caption: 'p',
     * }
     * @type {Object}
     */
    headlineMapping: PropTypes.object
  }

  static defaultProps = {
    color: 'textPrimary',
    variant: 'body2',
    headlineMapping: {
      h1: 'h1',
      h2: 'h2',
      h3: 'h3',
      h4: 'h4',
      h5: 'h5',
      h6: 'h6',
      subtitle1: 'h6',
      subtitle2: 'p',
      body1: 'p',
      body2: 'p',
      body3: 'p',
      body4: 'p',
      caption: 'p'
    }
  }

  render() {
    const {
      classes,
      className: classNameProp,
      variant,
      color,
      muiVariant,
      muiColor,
      muiClasses,
      component: componentProp,
      headlineMapping,
      ...rest
    } = this.props

    const className = classnames(
      classes[getSignalFontVariantName(variant)],
      {
        [classes[getColorKey(color)]]: color !== 'default'
      },
      classNameProp
    )
    const component = componentProp || headlineMapping[variant]

    return (
      <TypographyBase
        component={component}
        className={className}
        variant={muiVariant}
        color={muiColor}
        classes={muiClasses}
        {...rest}
      />
    )
  }
}

export default withCommonSnapshotsOnly(withStyles(styles)(Typography))
